import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import type { RootState } from "~/app/store";
import { requestAvailabilityData } from "~/domains/product/redux/actions";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import type { PackagingInformationEntry, ProductData } from "~/domains/product/types/ProductData";
import type {
  UseEffectivePackagingParams,
  UseEffectivePackagingResult,
} from "~/domains/product/types/EffectivePackaging";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  buildEffectivePackaging,
  buildSimpleEffectivePackagingEntry,
  type PredecessorDescriptorWithEntries,
} from "~/domains/product/utils/buildEffectivePackaging";

export interface UseEffectivePackagingResolvedResult extends UseEffectivePackagingResult {
  predecessors: PredecessorDescriptorWithEntries[];
}

function normalizePriceReference(priceReference: string) {
  return priceReference === "-" ? "" : priceReference;
}

function getActiveEntries(entries: PackagingInformationEntry[], restrictionPackCode?: string) {
  if (!restrictionPackCode) {
    return entries;
  }

  const filteredEntries = entries.filter((entry) => entry.packCode === restrictionPackCode);
  return filteredEntries.length > 0 ? filteredEntries : entries;
}

function resolveCrossProductPredecessors(
  descriptor: ProductDescriptor,
  activeEntries: PackagingInformationEntry[],
): ProductData[] {
  const mainProduct = descriptor.base;
  const predecessorProductsBySapMaterialNumber = new Map(
    mainProduct.predecessorProducts.map((product) => [product.sapMaterialNumber, product]),
  );
  const crossProductsByCode = new Map<string, ProductData>();

  activeEntries.forEach((entry) => {
    const fifteenDigitEntry = mainProduct.fifteenDigit[entry.packCode];

    if (!fifteenDigitEntry) {
      return;
    }

    if (fifteenDigitEntry.successor.slice(0, 13) === mainProduct.sapMaterialNumber) {
      return;
    }

    fifteenDigitEntry.predecessors.forEach((materialNo15) => {
      const sourceSapMaterialNumber = materialNo15.slice(0, 13);

      if (sourceSapMaterialNumber === mainProduct.sapMaterialNumber) {
        return;
      }

      const sourceProduct = predecessorProductsBySapMaterialNumber.get(sourceSapMaterialNumber);
      if (sourceProduct) {
        crossProductsByCode.set(sourceProduct.code, sourceProduct);
      }
    });
  });

  return Array.from(crossProductsByCode.values());
}

export function useEffectivePackaging({
  descriptor,
  priceReference,
  restrictionPackCode,
}: UseEffectivePackagingParams): UseEffectivePackagingResolvedResult {
  const dispatch = useDispatch();
  const normalizedPriceReference = normalizePriceReference(priceReference);
  const isPhaseoutEnabled = isPhaseOut2Enabled();

  const activeEntries = useMemo(
    () => getActiveEntries(descriptor?.availability?.packagingInformation ?? [], restrictionPackCode),
    [descriptor?.availability?.packagingInformation, restrictionPackCode],
  );

  const crossProductPredecessors = useMemo(() => {
    if (!descriptor || !isPhaseoutEnabled || !descriptor.availability) {
      return [];
    }

    return resolveCrossProductPredecessors(descriptor, activeEntries);
  }, [descriptor, activeEntries, isPhaseoutEnabled]);

  const crossProductDescriptors = useSelector((state: RootState): Array<ProductDescriptor | undefined> => {
    return crossProductPredecessors.map((product) => {
      return selectProductDescriptor(state, {
        productCode: product.code,
        priceReference: normalizedPriceReference,
      });
    });
  });

  useEffect(() => {
    if (!descriptor || !isPhaseoutEnabled) {
      return;
    }

    crossProductDescriptors
      .filter(
        (crossProductDescriptor) =>
          !crossProductDescriptor?.availability &&
          !crossProductDescriptor?.isAvailabilityLoading &&
          !crossProductDescriptor?.isAvailabilityError,
      )
      .forEach((crossProductDescriptor) => {
        if (!crossProductDescriptor) {
          return;
        }

        dispatch(requestAvailabilityData(crossProductDescriptor.base, true, undefined, true));
      });
  }, [descriptor, dispatch, crossProductDescriptors, isPhaseoutEnabled]);

  const status = (() => {
    if (!descriptor) {
      return "idle";
    }

    if (descriptor.isAvailabilityError || crossProductDescriptors.some((item) => item?.isAvailabilityError)) {
      return "error";
    }

    if (!descriptor.availability || descriptor.isAvailabilityLoading) {
      return "loading";
    }

    if (
      isPhaseoutEnabled &&
      crossProductDescriptors.some((item) => item?.isAvailabilityLoading || !item?.availability)
    ) {
      return "loading";
    }

    return "loaded";
  })();

  const predecessors = useMemo<PredecessorDescriptorWithEntries[]>(() => {
    if (!descriptor?.availability) {
      return [];
    }

    if (!isPhaseoutEnabled) {
      return [];
    }

    return [
      {
        descriptor,
        entries: descriptor.availability.packagingInformation,
      },
      ...crossProductDescriptors.flatMap((crossDescriptor) => {
        if (!crossDescriptor?.availability) {
          return [];
        }

        return {
          descriptor: crossDescriptor,
          entries: crossDescriptor.availability.packagingInformation,
        };
      }),
    ];
  }, [crossProductDescriptors, descriptor, isPhaseoutEnabled]);

  const entries = useMemo(() => {
    if (!descriptor?.availability || status !== "loaded") {
      return [];
    }

    if (!isPhaseoutEnabled) {
      return activeEntries.map((entry) => buildSimpleEffectivePackagingEntry(descriptor, entry));
    }

    return buildEffectivePackaging(activeEntries, descriptor, predecessors);
  }, [activeEntries, descriptor, isPhaseoutEnabled, predecessors, status]);

  const byPackCode = useMemo(() => {
    return entries.reduce<Record<string, (typeof entries)[number]>>((acc, entry) => {
      acc[entry.packCode] = entry;
      return acc;
    }, {});
  }, [entries]);

  return {
    status,
    entries,
    byPackCode,
    predecessors,
  };
}
